import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash, ForkKnife, ShieldCheck, Snowflake, Package, ThermometerHot, TrashSimple, Check, Sparkle, X, Warning, Timer, Camera } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import ScanModal from "@/components/ScanModal";

const LOCATIONS = [
    { v: "fridge", l: "Frigo" },
    { v: "freezer", l: "Congélateur" },
    { v: "pantry", l: "Placard" },
];
const CATEGORIES = ["Fruits", "Légumes", "Viandes", "Poissons", "Produits laitiers", "Œufs", "Céréales", "Boissons", "Épicerie", "Autre"];

function daysUntil(iso) {
    if (!iso) return null;
    const d = new Date(iso + "T00:00:00");
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.round((d - now) / 86400000);
}

function urgencyBadge(days) {
    if (days === null) return { label: "Sans DLC", cls: "bg-zinc-100 text-zinc-500" };
    if (days < 0) return { label: `Expiré ${Math.abs(days)}j`, cls: "bg-fzred text-white" };
    if (days === 0) return { label: "Aujourd'hui", cls: "bg-fzred text-white" };
    if (days <= 2) return { label: `${days}j`, cls: "bg-fzyellow text-zinc-900" };
    if (days <= 5) return { label: `${days}j`, cls: "bg-amber-100 text-amber-800" };
    return { label: `${days}j`, cls: "bg-fzgreen/10 text-fzgreen" };
}

export default function Dashboard() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [impact, setImpact] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [showScan, setShowScan] = useState(false);
    const [showRecipes, setShowRecipes] = useState(false);
    const [recipes, setRecipes] = useState("");
    const [genLoading, setGenLoading] = useState(false);
    const [hygiene, setHygiene] = useState({ open: false, item: null, text: "", loading: false });
    const [msg, setMsg] = useState("");

    const load = async () => {
        try {
            const [a, b] = await Promise.all([api.get("/fridge/items"), api.get("/stats/impact")]);
            setItems(a.data);
            setImpact(b.data);
        } catch (e) {
            setMsg(formatApiError(e));
        }
    };
    useEffect(() => { load(); }, []);

    const groups = useMemo(() => {
        const g = { fridge: [], freezer: [], pantry: [] };
        for (const it of items) (g[it.location] || g.fridge).push(it);
        return g;
    }, [items]);

    const urgents = useMemo(() => items.filter((it) => {
        const d = daysUntil(it.expiration_date);
        return d !== null && d <= 2;
    }), [items]);

    const generateRecipes = async () => {
        setShowRecipes(true);
        setRecipes("");
        setGenLoading(true);
        try {
            const res = await fetch(`${API}/ai/recipes/stream`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(localStorage.getItem("fz_token") ? { Authorization: `Bearer ${localStorage.getItem("fz_token")}` } : {}),
                },
                credentials: "include",
                body: JSON.stringify({ health_focus: true, max_time_min: 30 }),
            });
            if (!res.ok) {
                const t = await res.text();
                throw new Error(t || "Erreur IA");
            }
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                setRecipes((prev) => prev + dec.decode(value));
            }
        } catch (e) {
            setRecipes(`Erreur : ${e.message}`);
        } finally {
            setGenLoading(false);
        }
    };

    const openHygiene = async (item) => {
        setHygiene({ open: true, item, text: "", loading: true });
        try {
            const { data } = await api.post("/ai/hygiene", { item_name: item.name, expiration_date: item.expiration_date });
            setHygiene({ open: true, item, text: data.analysis, loading: false });
        } catch (e) {
            setHygiene({ open: true, item, text: `Erreur : ${formatApiError(e)}`, loading: false });
        }
    };

    const consume = async (id, action) => {
        try {
            await api.post(`/fridge/items/${id}/consume`, { action });
            await load();
        } catch (e) { setMsg(formatApiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Supprimer cet aliment ?")) return;
        try { await api.delete(`/fridge/items/${id}`); await load(); } catch (e) { setMsg(formatApiError(e)); }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="dashboard-page">
            <Navbar />

            <div className="mx-auto max-w-7xl px-6 py-8">
                {/* Header row */}
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="uppercase-tag mb-1">Tableau de bord</div>
                        <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">
                            Bonjour {user?.name || ""}.
                        </h1>
                        <p className="text-zinc-500 text-sm mt-1">Voici l'état de votre frigo, votre impact, et vos alertes.</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button data-testid="btn-scan" onClick={() => setShowScan(true)} className="btn-outline border-fzblue text-fzblue hover:bg-fzblue hover:text-white"><Camera size={16} weight="fill" /> Scanner mon frigo</button>
                        <button data-testid="btn-add-item" onClick={() => setShowAdd(true)} className="btn-primary"><Plus size={16} /> Ajouter un aliment</button>
                        <button data-testid="btn-generate-recipes" onClick={generateRecipes} disabled={items.length === 0} className="btn-outline disabled:opacity-40"><Sparkle size={16} weight="fill" /> Recettes IA</button>
                    </div>
                </div>

                {/* Impact stats */}
                {impact && (
                    <div className="grid md:grid-cols-4 gap-4 mb-8">
                        <StatCard testid="stat-active" label="Aliments actifs" value={impact.active_items} sub="dans mon inventaire" accent="text-fzblue" />
                        <StatCard testid="stat-kg" label="Kg sauvés" value={impact.kg_saved} sub={`${impact.items_saved} aliments consommés`} accent="text-fzgreen" />
                        <StatCard testid="stat-eur" label="€ économisés" value={`${impact.eur_saved} €`} sub="estimation" accent="text-zinc-900" />
                        <StatCard testid="stat-co2" label="CO₂ évité" value={`${impact.co2_saved_kg} kg`} sub="empreinte carbone" accent="text-fzblue" />
                    </div>
                )}

                {/* Chart + urgents */}
                <div className="grid md:grid-cols-3 gap-4 mb-8">
                    <div className="md:col-span-2 card-flat p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="uppercase-tag">Impact 14 derniers jours</div>
                            <div className="text-xs text-zinc-500 font-mono">consommé vs gaspillé</div>
                        </div>
                        <div className="h-56">
                            <ResponsiveContainer>
                                <LineChart data={impact?.timeline || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717a" }} hide />
                                    <YAxis tick={{ fontSize: 10, fill: "#71717a" }} />
                                    <Tooltip contentStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="consumed" stroke="#00C853" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="wasted" stroke="#D50000" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="card-flat p-6">
                        <div className="uppercase-tag mb-3 flex items-center gap-2"><Warning size={12} /> URGENTS</div>
                        {urgents.length === 0 ? (
                            <div className="text-sm text-zinc-500">Aucun aliment n'expire dans les 48h. 👌</div>
                        ) : (
                            <ul className="space-y-2">
                                {urgents.map((it) => {
                                    const d = daysUntil(it.expiration_date);
                                    const b = urgencyBadge(d);
                                    return (
                                        <li key={it.id} className="flex items-center justify-between">
                                            <span className="text-sm">{it.name}</span>
                                            <span className={`text-xs px-2 py-1 ${b.cls}`}>{b.label}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Fridge items grouped */}
                <div className="space-y-8">
                    <FridgeGroup title="Frigo" icon={<Snowflake size={16} />} items={groups.fridge}
                        onConsume={consume} onRemove={remove} onHygiene={openHygiene} testid="group-fridge" />
                    <FridgeGroup title="Congélateur" icon={<ThermometerHot size={16} />} items={groups.freezer}
                        onConsume={consume} onRemove={remove} onHygiene={openHygiene} testid="group-freezer" />
                    <FridgeGroup title="Placard" icon={<Package size={16} />} items={groups.pantry}
                        onConsume={consume} onRemove={remove} onHygiene={openHygiene} testid="group-pantry" />
                </div>

                {msg && <div className="mt-6 text-fzred text-sm" data-testid="dash-error">{msg}</div>}
            </div>

            {showAdd && <AddItemModal onClose={() => setShowAdd(false)} onDone={async () => { setShowAdd(false); await load(); }} />}
            {showScan && <ScanModal onClose={() => setShowScan(false)} onAdded={async () => { setShowScan(false); await load(); }} />}
            {showRecipes && <RecipesModal loading={genLoading} content={recipes} onClose={() => setShowRecipes(false)} />}
            {hygiene.open && <HygieneModal state={hygiene} onClose={() => setHygiene({ ...hygiene, open: false })} />}
        </div>
    );
}

function StatCard({ label, value, sub, accent = "text-zinc-900", testid }) {
    return (
        <div data-testid={testid} className="card-flat p-6">
            <div className="uppercase-tag">{label}</div>
            <div className={`font-display font-black text-4xl mt-2 ${accent}`}>{value}</div>
            <div className="text-xs text-zinc-500 mt-1">{sub}</div>
        </div>
    );
}

function FridgeGroup({ title, icon, items, onConsume, onRemove, onHygiene, testid }) {
    return (
        <div data-testid={testid}>
            <div className="flex items-center gap-2 mb-3 uppercase-tag">
                {icon}<span>{title}</span><span className="ml-2 text-zinc-400">· {items.length}</span>
            </div>
            {items.length === 0 ? (
                <div className="border border-dashed border-zinc-300 py-8 text-center text-sm text-zinc-500">
                    Rien pour l'instant. Ajoutez un aliment ci-dessus.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((it) => {
                        const d = daysUntil(it.expiration_date);
                        const b = urgencyBadge(d);
                        return (
                            <div key={it.id} data-testid={`item-${it.id}`} className="card-flat p-4 hover:-translate-y-0.5 transition-transform">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="font-display font-semibold">{it.name}</div>
                                        <div className="text-xs text-zinc-500">
                                            {it.quantity} {it.unit} {it.category ? "· " + it.category : ""}
                                        </div>
                                    </div>
                                    <span className={`text-[10px] font-mono px-2 py-1 ${b.cls}`}>{b.label}</span>
                                </div>
                                {it.notes && <div className="text-xs text-zinc-500 mt-2 italic">{it.notes}</div>}
                                <div className="flex flex-wrap gap-2 mt-4">
                                    <button data-testid={`hyg-${it.id}`} onClick={() => onHygiene(it)} className="text-xs inline-flex items-center gap-1 border border-zinc-300 px-2 py-1 hover:bg-zinc-100"><ShieldCheck size={12} /> Hygiène IA</button>
                                    <button data-testid={`consume-${it.id}`} onClick={() => onConsume(it.id, "consumed")} className="text-xs inline-flex items-center gap-1 bg-fzgreen text-white px-2 py-1 hover:opacity-90"><Check size={12} /> Consommé</button>
                                    <button data-testid={`waste-${it.id}`} onClick={() => onConsume(it.id, "wasted")} className="text-xs inline-flex items-center gap-1 bg-fzred/90 text-white px-2 py-1 hover:opacity-90"><TrashSimple size={12} /> Gaspillé</button>
                                    <button data-testid={`del-${it.id}`} onClick={() => onRemove(it.id)} className="text-xs inline-flex items-center gap-1 text-zinc-500 hover:text-fzred ml-auto"><Trash size={12} /></button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function AddItemModal({ onClose, onDone }) {
    const [form, setForm] = useState({
        name: "", quantity: 1, unit: "u", location: "fridge",
        category: "Autre", expiration_date: "", notes: "",
    });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            const body = { ...form };
            if (!body.expiration_date) delete body.expiration_date;
            await api.post("/fridge/items", body);
            onDone();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <Modal onClose={onClose} title="Ajouter un aliment" testid="modal-add-item">
            <form onSubmit={submit} className="space-y-4" data-testid="add-item-form">
                <Field label="Nom">
                    <input data-testid="add-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full border border-zinc-300 px-3 py-2" placeholder="Poulet, tomates, yaourt..." />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Quantité">
                        <input data-testid="add-qty" type="number" min={0} step={0.1} value={form.quantity}
                            onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })}
                            className="w-full border border-zinc-300 px-3 py-2" />
                    </Field>
                    <Field label="Unité">
                        <select data-testid="add-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                            className="w-full border border-zinc-300 px-3 py-2 bg-white">
                            {["u", "g", "kg", "ml", "L", "boîte", "sachet"].map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Emplacement">
                        <select data-testid="add-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                            className="w-full border border-zinc-300 px-3 py-2 bg-white">
                            {LOCATIONS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                        </select>
                    </Field>
                    <Field label="Catégorie">
                        <select data-testid="add-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                            className="w-full border border-zinc-300 px-3 py-2 bg-white">
                            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                    </Field>
                </div>
                <Field label="DLC (date limite)">
                    <input data-testid="add-dlc" type="date" value={form.expiration_date}
                        onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                        className="w-full border border-zinc-300 px-3 py-2" />
                </Field>
                <Field label="Notes">
                    <input data-testid="add-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="w-full border border-zinc-300 px-3 py-2" placeholder="Optionnel" />
                </Field>
                {err && <div className="text-fzred text-sm">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
                    <button data-testid="add-submit" type="submit" disabled={loading} className="btn-primary">
                        {loading ? "Ajout..." : "Ajouter"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function RecipesModal({ loading, content, onClose }) {
    return (
        <Modal onClose={onClose} title="Chef IA · Recettes anti-gaspi" testid="modal-recipes" wide>
            <div className="uppercase-tag flex items-center gap-2 mb-4">
                <Sparkle size={12} weight="fill" className="text-fzblue" /> GPT-5.2 · Streaming
                {loading && <span className="ml-2 flex items-center gap-1 text-fzblue"><span className="w-1.5 h-1.5 bg-fzblue rounded-full animate-pulse" /> génération...</span>}
            </div>
            <div data-testid="recipes-output" className="prose prose-sm max-w-none whitespace-pre-wrap font-sans text-zinc-800 leading-relaxed min-h-[300px]">
                {content || (loading ? "L'IA analyse votre frigo..." : "Aucune recette générée.")}
            </div>
        </Modal>
    );
}

function HygieneModal({ state, onClose }) {
    return (
        <Modal onClose={onClose} title={`Hygiène · ${state.item?.name}`} testid="modal-hygiene">
            {state.loading ? (
                <div className="text-zinc-500 text-sm">Analyse IA en cours...</div>
            ) : (
                <div data-testid="hygiene-output" className="whitespace-pre-wrap text-sm leading-relaxed">{state.text}</div>
            )}
        </Modal>
    );
}

function Modal({ children, onClose, title, testid, wide = false }) {
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-50 bg-zinc-950/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid} onClick={onClose}>
            <div className={"bg-white border border-zinc-200 w-full " + (wide ? "max-w-3xl" : "max-w-md")} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-zinc-200">
                    <div className="font-display font-bold">{title}</div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
                </div>
                <div className="p-6 max-h-[75vh] overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="uppercase-tag mb-1">{label}</div>
            {children}
        </label>
    );
}
